import React, { useEffect, useRef, useState } from "react";
import "./CreateComplainProgressBar.scss";
import { useSelector } from "react-redux";
import {
  getComplaintFiledStatus,
  getInitialPreTrialData,
  useComplaintMainReducer,
} from "../../redux/slice/complaintMainSlice";
import { useAppDispatch } from "../../hooks/hooks";
import { useNavigate } from "react-router-dom";

interface ProgressBarProps {
  duration: number;
  shouldShowBar: boolean;
  openState: (state: any) => void;
}

const CreateComplaintProgressBar: React.FC<ProgressBarProps> = ({
  duration,
  shouldShowBar,
  openState,
}) => {
  const [showSuccess, setShowSuccess] = useState(false); // KHL PR-11778 10/23/2025
  const [progress, setProgress] = useState(0);
  const [showPendingMessage, setShowPendingMessage] = useState(false);
  const [showButtons, setShowButtons] = useState(false);
  const [hideProgressBar, setHideProgressBar] = useState(false);
  const [showWaitMessage, setShowWaitMessage] = useState(true);
  const [status, setStatus] = useState<string>("");
  const complaintMain = useComplaintMainReducer();
  const ecomplaintId = useSelector(
    (state: any) => complaintMain.complaint.ecomplaintId
  );

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const messages = [
    <>
      Case filing still pending with Circuit Clerk. Please <br /> wait...
    </>,
    <>
      We're talking to the court clerk system... <br /> hang tight!
    </>,
    <>
      Your complaint is in the queue for processing. <br /> This may take a
      moment.
    </>,
    <>
      Court clerk is processing your case... <br /> thanks for your patience.
    </>,
    <>
      Finalizing the submission to the clerk's system... <br /> please wait.
    </>,
  ];
  const [currentMessage, setCurrentMessage] = useState(messages[0]);

  useEffect(() => {
    if (!shouldShowBar || duration <= 0) return;

    const intervalTime = (duration * 1000) / 100;
    const interval = setInterval(() => {
      setProgress((prev) => {
        const next = prev + 1;
        setShowWaitMessage(true);
        if (next >= 100) {
          clearInterval(interval);

          dispatch(getComplaintFiledStatus({ ecomplaintId }))
            .unwrap()
            .then((resStatus: string) => {
              setStatus(resStatus);

              if (resStatus === "ASSIGNED") {
    // KHL PR-11778 10/23/2025 START 
                setShowSuccess(true);
                setHideProgressBar(true);
                setShowPendingMessage(false);
                setShowWaitMessage(false);
                setShowButtons(false);
    // KHL PR-11778 10/23/2025 END
              } else if (resStatus === "COMMUNICATION ERROR") {
                setShowPendingMessage(true);
                setHideProgressBar(true);
                setShowButtons(true);
                setShowWaitMessage(false);
              } else {
                setShowPendingMessage(true);
                setShowWaitMessage(false);
                retryCheckStatus(10);
              }
            })
            .catch((error) => {
              console.error("Error fetching complaint filed status:", error);
              setShowPendingMessage(true);
              setHideProgressBar(true);
              setShowButtons(true);
              setShowWaitMessage(false); 
            });

          return 100;
        }
        return next;
      });
    }, intervalTime);

    return () => clearInterval(interval);
  }, [duration, shouldShowBar, dispatch, ecomplaintId]);

  const startProgress = (ms: number) => {
    setHideProgressBar(false);
    setProgress(0);
    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);

    const startTime = Date.now();
    progressIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const percent = Math.min((elapsed / ms) * 100, 100);
      setProgress(Math.floor(percent));

      if (percent >= 100) {
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
      }
    }, 100);
  };

  const retryCheckStatus = (retriesLeft: number, retryNumber: number = 1) => {
    if (retriesLeft <= 0) {
      setShowButtons(true);
      setHideProgressBar(true);
      setShowWaitMessage(false);
      return;
    }

    const messageIndex = (retryNumber - 1) % messages.length;
    setCurrentMessage(messages[messageIndex]);
    const waitTime = 10000;
    startProgress(waitTime);
    setTimeout(() => {
      dispatch(getComplaintFiledStatus({ ecomplaintId }))
        .unwrap()
        .then((status: string) => {
          if (status === "ASSIGNED") {
    // KHL PR-11778 10/23/2025 START
              setShowSuccess(true);
              setHideProgressBar(true);
              setShowPendingMessage(false);
              setShowWaitMessage(false);
              setShowButtons(false);
    // KHL PR-11778 10/23/2025 END
            return;
          }
          if (status === "COMMUNICATION ERROR") {
            setStatus(status);
            setShowPendingMessage(true);
            setHideProgressBar(true);
            setShowButtons(true);
            setShowWaitMessage(false);
            return;
          }
          retryCheckStatus(retriesLeft - 1, retryNumber + 1);
        })
        .catch((error) => {
          console.error("Error fetching complaint filed status:", error);
        });
    }, waitTime);
  };

  const handleKeepWaiting = () => {
    setShowButtons(false);
    setShowPendingMessage(true);
    setShowWaitMessage(false);
    retryCheckStatus(10);
  };
// KHL PR-11778 10/23/2025 START
  const handleShowSuccessAlert = () => {
    dispatch(
      getInitialPreTrialData({
        ecomplaintId: ecomplaintId,
        navigate,
      })
    );
  };
// KHL PR-11778 10/23/2025 END
  if (!shouldShowBar) return null;

  return (
<div className="progress-overlay">
  <div className="progress-modal">
{/* KHL PR-11778 10/23/2025 START */}
    {showSuccess ? (
      <div className="success-modal">
        <h3 className="showSuccess-modal-title">Complaint Filed Successfully</h3>
        <p className="showSuccess-txt1">
            Your complaint has been successfully filed with the Circuit Court.
        </p>
        <p className="showSuccess-txt2">
            Please take one of the following actions:
        </p>
        <div className="showSuccess-button-row">
          <button
            className="secondary-btn"
            onClick={() => navigate("/EComplaintDashboard")}
          >
            Do It Later
          </button>
          <button
            className="primary-btn"
            onClick={handleShowSuccessAlert}
          >
            File "Release on Own Recognizance" Form
          </button>
        </div>
      </div>
    ) : (
      <>
{/* KHL PR-11778 10/23/2025 END */}      
        <p className="modal-title">Filing with Circuit Clerk</p>
        <hr className="title-divider" />

        <>
          {status === "COMMUNICATION ERROR" ? (
            <div className="icon_danger">
              <img src={require("../../assets/danger_image.png")} alt="stop" />
            </div>
          ) : (
            <div className="icon_processing">
              <img
                src={require("../../assets/progress_image.gif")}
                alt="Progress"
              />
            </div>
          )}
        </>

        {showWaitMessage && (
          <div className="alertTxt2">
            <p>
              Your case is being submitted…
              <br />
              this may take a few moments.
            </p>
          </div>
        )}

        {showPendingMessage && (
          <>
            {status === "COMMUNICATION ERROR" ? (
              <div className="alertTxt1">
                <p>
                  Communication to Circuit Clerk systems is down, please try
                  again later or complete and return a paper Release on
                  Recognizance form.
                  <br />
                  <br />
                  <span style={{ color: "rgb(202, 9, 9)" }}>Warning: </span>
                  Failure to file the release on recognizance will result in
                  your case not being scheduled for court!
                </p>
              </div>
            ) : (
              <div className="alertTxt2">
                <p>{currentMessage}</p>
              </div>
            )}
          </>
        )}

        {!hideProgressBar && (
          <div className="status-box">
            <div className="status-header">
              <span className="percentage-box">{progress} %</span>
              <span className="status-text">Filing Case... Please Wait.</span>
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
        )}

        <hr
          className="button-divider"
          style={{ display: showButtons ? "block" : "none" }}
        />
        {showButtons && (
          <div className="button-row" style={{ display: "flex" }}>
            {status !== "COMMUNICATION ERROR" && (
              <button className="wait-btn" onClick={handleKeepWaiting}>
                Keep Waiting
              </button>
            )}
            <button
              className="close-btn"
              onClick={() => {
                if (status === "COMMUNICATION ERROR") {
                  navigate("/EComplaintDashboard");
                } else {
                  openState(false);
                }
              }}
            >
              Close
            </button>
          </div>
        )}
      </>
    )}
  </div>
</div>

  );
};

export default CreateComplaintProgressBar;
